import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Minus,
  Plus,
  Trash2,
  Tag,
  Lock,
  ShoppingBag,
  MapPin,
  Phone,
  Loader2,
  CreditCard,
  AlertTriangle,
  Check,
  Shield,
  DoorOpen,
  KeyRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AddressAutocomplete } from '../../components/ui/AddressAutocomplete';
import { CheckoutSummaryCard } from '../../components/shopper/CheckoutSummaryCard';
import { useCartContext } from '../../hooks/useCart';
import { useDelivery } from '../../hooks/useDelivery';
// Order creation now handled server-side by startCheckout callable
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, functions } from '../../config/firebase';
import { toE164US } from '../../lib/utils';
import { formatEtaAsWindow } from '../../lib/deliveryEta';
import { useAuth } from '../../hooks/useAuth';
import { useSmartBack } from '../../hooks/useSmartBack';
import { BackButton } from '../../components/ui/BackButton';
import { ShopperBottomNav } from '../../components/shopper/ShopperBottomNav';
import { SHOPPER_HOME } from '../../constants/routes';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { getStripe, STRIPE_APPEARANCE } from '../../lib/stripeClient';
import { CardBrandMark } from '../../components/shopper/CardBrandMark';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import { formatCardBrand, formatExpiry, isCardExpired } from '../../types/payment';
import type { SavedPaymentMethod } from '../../types/payment';

/** Sentinel for the "enter a different card" option in the method picker. */
const NEW_CARD = 'new';

// ── Inner Checkout Form (needs Stripe Elements context) ─────
// Reuses the single PaymentIntent created by BagPage — never mints a second one.
//
// Saved cards are only offered at boutiques that settle through Stripe
// Connect: those charges run on the FASH platform account, which is where
// the shopper's saved cards live. Stores on their own Stripe keys get the
// card form only.
function CheckoutForm({
  orderId,
  clientSecret,
  savedMethods,
  defaultPaymentMethodId,
  supportsSavedCards,
  saveCard,
  savePreferenceBusy,
  onToggleSaveCard,
  onSuccess,
  onError,
}: {
  orderId: string;
  clientSecret: string;
  savedMethods: SavedPaymentMethod[];
  defaultPaymentMethodId: string | null;
  supportsSavedCards: boolean;
  saveCard: boolean;
  savePreferenceBusy: boolean;
  onToggleSaveCard: (next: boolean) => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);
  const [newCardReady, setNewCardReady] = useState(false);

  // 'new' means "enter a different card". Anything else is a saved card id.
  const [selectedId, setSelectedId] = useState<string>(() => {
    if (!savedMethods.length) return NEW_CARD;
    return defaultPaymentMethodId || savedMethods[0].id;
  });

  // The saved list arrives with the PaymentIntent, so it can land after the
  // first render. Only adopt it while the shopper has not chosen for themselves.
  const touchedSelection = useRef(false);
  useEffect(() => {
    if (touchedSelection.current) return;
    setSelectedId(savedMethods.length ? defaultPaymentMethodId || savedMethods[0].id : NEW_CARD);
  }, [savedMethods, defaultPaymentMethodId]);

  const usingNewCard = selectedId === NEW_CARD;

  const selectMethod = (id: string) => {
    touchedSelection.current = true;
    setSelectedId(id);
  };

  const handleSubmit = async () => {
    if (!stripe || !orderId || !clientSecret) return;

    if (usingNewCard && !elements) return;

    setConfirming(true);

    try {
      const result = usingNewCard
        ? await stripe.confirmPayment({
            elements: elements!,
            confirmParams: { return_url: `${window.location.origin}/orders` },
            // Keeps the shopper on the page unless the bank forces 3DS.
            redirect: 'if_required',
          })
        : await stripe.confirmCardPayment(clientSecret, { payment_method: selectedId });

      const error = result.error;
      const paymentIntent = result.paymentIntent;

      if (error) {
        onError(error.message || 'Card authorization failed');
        setConfirming(false);
        return;
      }

      if (
        paymentIntent?.status === 'requires_capture' ||
        paymentIntent?.status === 'succeeded'
      ) {
        // Tell backend the authorization succeeded
        const confirmAuth = httpsCallable(functions, 'confirmPaymentAuthorization');
        await confirmAuth({
          orderId,
          paymentIntentId: paymentIntent.id,
        });

        onSuccess();
      } else {
        onError(`Unexpected payment status: ${paymentIntent?.status}`);
      }
    } catch (err: any) {
      onError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const canSubmit = !!stripe && !confirming && (usingNewCard ? newCardReady : true);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 shadow-soft space-y-3">
        <div className="flex items-center space-x-3 mb-1">
          <CreditCard className="h-5 w-5 text-primary" />
          <span className="font-medium text-heading">Payment Method</span>
        </div>

        {savedMethods.map((method) => {
          const selected = selectedId === method.id;
          const expiry = formatExpiry(method.expMonth, method.expYear);
          const expired = isCardExpired(method.expMonth, method.expYear);
          return (
            <button
              key={method.id}
              type="button"
              onClick={() => selectMethod(method.id)}
              disabled={expired}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all disabled:opacity-50 ${
                selected
                  ? 'border-primary bg-purple-50/60 ring-2 ring-primary/20'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <CardBrandMark brand={method.brand} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-heading">
                  {formatCardBrand(method.brand)} &bull;&bull;&bull;&bull; {method.last4}
                </p>
                <p className={`text-xs ${expired ? 'text-error' : 'text-body'}`}>
                  {expired ? `Expired ${expiry}` : expiry ? `Expires ${expiry}` : 'Card on file'}
                </p>
              </div>
              {selected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
            </button>
          );
        })}

        {savedMethods.length > 0 && (
          <button
            type="button"
            onClick={() => selectMethod(NEW_CARD)}
            className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
              usingNewCard
                ? 'border-primary bg-purple-50/60 ring-2 ring-primary/20'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="h-8 w-12 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Plus className="h-4 w-4 text-gray-500" />
            </div>
            <span className="text-sm font-medium text-heading flex-1">
              Use a different payment method
            </span>
            {usingNewCard && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
          </button>
        )}

        {/* Mounted only while in use; Stripe wallets appear here automatically. */}
        {usingNewCard && (
          <div className="pt-1">
            <PaymentElement
              options={{ layout: 'tabs' }}
              onChange={(e) => setNewCardReady(e.complete)}
            />
          </div>
        )}

        {usingNewCard && supportsSavedCards && (
          <label className="flex items-center gap-2.5 pt-1 cursor-pointer">
            <input
              type="checkbox"
              checked={saveCard}
              disabled={savePreferenceBusy}
              onChange={(e) => onToggleSaveCard(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/30"
            />
            <span className="text-sm text-body">Save this card for future purchases</span>
            {savePreferenceBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
          </label>
        )}

        <div className="flex items-center gap-2 text-xs text-body pt-1">
          <Shield className="h-3.5 w-3.5 text-primary flex-shrink-0" />
          <span>
            Card is authorized only. You&apos;re charged after courier pickup.
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        size="lg"
        fullWidth
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="shadow-lg"
      >
        {confirming ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Authorizing...
          </>
        ) : (
          <>
            <Lock className="h-4 w-4 mr-2" />
            Authorize Payment
          </>
        )}
      </Button>

      <p className="text-xs text-center text-body">
        Funds held temporarily. Charged only after courier pickup.
      </p>
    </div>
  );
}

// ── Delivery quote timing ───────────────────────────────────
/** Wait for the shopper to stop typing before spending an Uber quote call. */
const ESTIMATE_DEBOUNCE_MS = 800;
/** Re-quote this far ahead of the stated expiry. */
const QUOTE_REFRESH_LEAD_MS = 30_000;
/** Used when Uber omits an expiry; their quotes last roughly five minutes. */
const QUOTE_REFRESH_FALLBACK_MS = 4 * 60_000;
/** Floor on the refresh interval, so an already-expired quote can't spin. */
const MIN_QUOTE_REFRESH_MS = 60_000;

// ── Saved address shape (users/{uid}/addresses) ─────────────
interface SavedAddress {
  id: string;
  label: string;
  street: string;
  apt?: string;
  city: string;
  state: string;
  zip: string;
  isDefault: boolean;
}

// ── Main BagPage Component ──────────────────────────────────
export function BagPage() {
  const navigate = useNavigate();
  const goBack = useSmartBack(SHOPPER_HOME);
  const cart = useCartContext();
  const { user } = useAuth();
  const { estimateFee, clearError, estimateLoading, error: deliveryError } = useDelivery();
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Promo code state — validated server-side against promo_codes
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState<{
    code: string;
    discountCents: number;
    description: string;
  } | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);

  // Saved addresses (from Settings → Addresses)
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);

  // Checkout flow state
  const [checkoutStep, setCheckoutStep] = useState<'cart' | 'payment'>('cart');
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [serverTotal, setServerTotal] = useState<number | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Saved cards for this order, returned alongside the PaymentIntent so the
  // picker reflects the account the charge will actually run on.
  const [savedMethods, setSavedMethods] = useState<SavedPaymentMethod[]>([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
  const [supportsSavedCards, setSupportsSavedCards] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [savePreferenceBusy, setSavePreferenceBusy] = useState(false);

  // Default card for the summary row on the cart step, before any order exists.
  const { methods: profileMethods, defaultId: profileDefaultId } = usePaymentMethods();
  const summaryCard =
    profileMethods.find((m) => m.id === profileDefaultId) || profileMethods[0] || null;

  // Delivery address state (lat/lng captured from Google Places when available,
  // so Uber Direct can pin the exact dropoff instead of re-geocoding the text)
  const [address, setAddress] = useState<{
    street: string;
    city: string;
    state: string;
    zipCode: string;
    lat?: number;
    lng?: number;
  }>({
    street: '',
    city: '',
    state: '',
    zipCode: '',
  });

  // Customer phone for delivery courier contact
  const [customerPhone, setCustomerPhone] = useState('');

  // Delivery details (apt, access codes, instructions)
  const [apt, setApt] = useState('');
  const [gateCodeEnabled, setGateCodeEnabled] = useState(false);
  const [gateCode, setGateCode] = useState('');
  const [buildingCodeEnabled, setBuildingCodeEnabled] = useState(false);
  const [buildingCode, setBuildingCode] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');

  // Delivery method & tip
  const [dropoffType, setDropoffType] = useState<'DOOR' | 'CURBSIDE' | 'LEAVE_AT_DOOR'>('DOOR');
  const [driverTip, setDriverTip] = useState<number>(0);
  const [customTipInput, setCustomTipInput] = useState('');
  // Uber delivery quote (replaces hardcoded $25). Fee and ETA are kept in one
  // object so editing the address clears both together — a fee from the old
  // address paired with an ETA from the new one would be worse than neither.
  const [estimate, setEstimate] = useState<{
    feeCents: number;
    etaIso: string | null;
  } | null>(null);
  const deliveryFee = estimate?.feeCents ?? null;
  const deliveryEta = estimate?.etaIso ?? null;
  const [quoteId, setQuoteId] = useState<string | null>(null);

  const addressComplete = !!(address.street && address.city && address.state && address.zipCode);
  // Valid only when it normalizes to a 10-digit US number (E.164). Prevents
  // sending Uber a malformed dropoff phone that would fail courier contact.
  const phoneValid = toE164US(customerPhone) !== '';
  const displayFee = deliveryFee !== null ? deliveryFee / 100 : 0;
  const displayDiscount = promoApplied
    ? Math.min(promoApplied.discountCents / 100, cart.subtotal)
    : 0;
  // Customer pays subtotal − discount + delivery + tip (matches server-side pricing)
  const total = Math.max(0, cart.subtotal - displayDiscount) + displayFee + (driverTip || 0);

  // Uber hands back an ISO timestamp; it has to be turned into a clock-time
  // window before it reaches the shopper. The quote error gets its own banner
  // below, so this slot only ever describes the state of the estimate.
  const arrivalWindow = formatEtaAsWindow(deliveryEta);
  const deliveryEstimateLabel = arrivalWindow
    ? `Arrives ${arrivalWindow}`
    : deliveryError
      ? 'Unavailable for this address'
      : deliveryFee !== null
        ? 'Same-day delivery'
        : addressComplete
          ? 'Calculating your arrival window'
          : 'Enter your address for an arrival window';

  // ── Load saved addresses; auto-fill the default one ───────
  useEffect(() => {
    if (!user) return;
    getDocs(query(collection(db, 'users', user.uid, 'addresses'), orderBy('isDefault', 'desc')))
      .then((snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as SavedAddress[];
        setSavedAddresses(list);

        const preferred = list.find((a) => a.isDefault) || list[0];
        if (preferred) {
          // Only auto-fill if the shopper hasn't typed anything yet
          setAddress((current) => {
            if (current.street || current.city || current.zipCode) return current;
            setSelectedAddressId(preferred.id);
            setApt(preferred.apt || '');
            return {
              street: preferred.street,
              city: preferred.city,
              state: preferred.state,
              zipCode: preferred.zip,
            };
          });
        }
      })
      .catch(() => {});
  }, [user]);

  const applySavedAddress = (addr: SavedAddress) => {
    setSelectedAddressId(addr.id);
    setAddress({
      street: addr.street,
      city: addr.city,
      state: addr.state,
      zipCode: addr.zip,
    });
    setApt(addr.apt || '');
  };

  // ── Promo code validation (server-side) ───────────────────
  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code || promoLoading) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const validate = httpsCallable(functions, 'validatePromoCode');
      const result = await validate({
        code,
        subtotalCents: Math.round(cart.subtotal * 100),
      });
      const data = result.data as { code: string; discountCents: number; description: string };
      setPromoApplied(data);
      setPromoCode('');
    } catch (err: any) {
      setPromoApplied(null);
      setPromoError(err?.message || 'Could not validate this code.');
    } finally {
      setPromoLoading(false);
    }
  };

  // ── Delivery estimate (debounced, self-refreshing) ────────
  // Uber quotes expire in about five minutes and the ETA they carry is an
  // absolute time, so a quote fetched while the shopper was still filling in
  // their phone number would promise a window that has already passed.

  // Guards against a slow response for an old address overwriting a newer one.
  const estimateRequestRef = useRef(0);

  const addressKey = `${address.street}|${address.city}|${address.state}|${address.zipCode}`;
  const businessId = cart.items[0]?.businessId || cart.items[0]?.brand || '';

  // Drop the previous quote the moment the destination changes, so a stale fee
  // and arrival window never sit under an address they weren't quoted for.
  useEffect(() => {
    estimateRequestRef.current += 1;
    setEstimate(null);
    clearError();
  }, [addressKey, businessId, clearError]);

  // Once startCheckout has priced the order, its server-verified numbers win.
  const quotingEnabled = addressComplete && !!businessId && checkoutStep === 'cart' && !orderId;

  useEffect(() => {
    if (!quotingEnabled) return;

    const requestId = estimateRequestRef.current;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const fetchEstimate = async () => {
      const result = await estimateFee(businessId, {
        street: address.street,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
      });
      if (cancelled || estimateRequestRef.current !== requestId) return;

      setEstimate(result ? { feeCents: Math.round(result.fee * 100), etaIso: result.eta ?? null } : null);
      if (!result) return;

      // Re-quote shortly before the current one lapses so the window stays live.
      const expiresAt = result.expiresAt ? new Date(result.expiresAt).getTime() : NaN;
      const msUntilRefresh = isNaN(expiresAt)
        ? QUOTE_REFRESH_FALLBACK_MS
        : Math.max(MIN_QUOTE_REFRESH_MS, expiresAt - Date.now() - QUOTE_REFRESH_LEAD_MS);
      timer = setTimeout(fetchEstimate, msUntilRefresh);
    };

    timer = setTimeout(fetchEstimate, ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Address fields are folded into addressKey so object identity can't retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotingEnabled, addressKey, businessId, estimateFee]);

  // Load business delivery defaults (dropoff type)
  useEffect(() => {
    const businessId = cart.items[0]?.businessId || cart.items[0]?.brand || '';
    if (!businessId) return;

    getDoc(doc(db, 'businesses', businessId)).then((snap) => {
      if (!snap.exists()) return;
      const biz = snap.data();
      const defaultType = biz?.fulfillment?.defaultDropoffType;
      if (defaultType && ['DOOR', 'CURBSIDE', 'LEAVE_AT_DOOR'].includes(defaultType)) {
        setDropoffType(defaultType);
      }
    }).catch(() => {});
  }, [cart.items]);

  // ── Step 1: Server-side checkout → create order + reserve inventory + price ──
  const handleProceedToPayment = async () => {
    if (!user || !addressComplete) return;
    if (!phoneValid) {
      setPaymentError('Please enter a valid 10-digit US phone number for delivery updates.');
      return;
    }
    setCreatingOrder(true);
    setPaymentError(null);

    try {
      // Call server-side checkout orchestration
      // This validates cart, reserves inventory (15-min TTL), gets Uber quote, prices the order
      // Compose delivery notes from structured fields
      const notesParts: string[] = [];
      if (gateCode.trim()) notesParts.push(`Gate code: ${gateCode.trim()}`);
      if (buildingCode.trim()) notesParts.push(`Building code: ${buildingCode.trim()}`);
      if (deliveryInstructions.trim()) notesParts.push(deliveryInstructions.trim());
      const deliveryNotes = notesParts.join(' | ').slice(0, 280);

      const startCheckout = httpsCallable(functions, 'startCheckout');
      const checkoutResult = await startCheckout({
        items: cart.items.map((item) => ({
          productId: item.productId,
          name: item.name,
          brand: item.brand,
          price: item.price,
          size: item.size,
          quantity: item.quantity,
          imageUrl: item.imageUrl,
        })),
        deliveryAddress: { ...address, apt: apt.trim() || undefined },
        deliveryNotes: deliveryNotes || undefined,
        customerName: user.displayName || '',
        customerPhone: toE164US(customerPhone),
        businessId: cart.items[0]?.businessId || cart.items[0]?.brand || '',
        dropoffType,
        driverTip: driverTip > 0 ? Math.round(driverTip * 100) : undefined,
        promoCode: promoApplied?.code || undefined,
      });

      const checkout = checkoutResult.data as {
        orderId: string;
        subtotal: number;
        deliveryFee: number;
        platformFee: number;
        total: number;
        deliveryEta: string | null;
      };

      setOrderId(checkout.orderId);
      // Update displayed pricing with server-verified values
      setEstimate({
        feeCents: Math.round(checkout.deliveryFee * 100),
        etaIso: checkout.deliveryEta ?? null,
      });
      setServerTotal(checkout.total);

      // Create the ONE PaymentIntent for this order — its client secret is
      // reused by the payment form (no second intent is ever created).
      const createPI = httpsCallable(functions, 'createCheckoutPaymentIntent');
      const result = await createPI({ orderId: checkout.orderId, savePaymentMethod: false });
      const data = result.data as {
        clientSecret: string;
        publishableKey: string;
        supportsSavedCards?: boolean;
        savedPaymentMethods?: SavedPaymentMethod[];
        defaultPaymentMethodId?: string | null;
      };

      const stripe = await getStripe(data.publishableKey);
      setStripeInstance(stripe);
      setClientSecret(data.clientSecret);
      setSupportsSavedCards(data.supportsSavedCards === true);
      setSavedMethods(data.savedPaymentMethods || []);
      setDefaultPaymentMethodId(data.defaultPaymentMethodId || null);
      setSaveCard(false);
      setCheckoutStep('payment');
    } catch (err: any) {
      console.error('Checkout error:', err);
      // Firebase callable errors expose a `code` like "functions/out-of-range".
      const code: string = err?.code || '';
      if (code.includes('out-of-range')) {
        setPaymentError(
          'This delivery address is outside our delivery area. Please choose a closer address.'
        );
      } else if (code.includes('invalid-argument') && /phone/i.test(err?.message || '')) {
        setPaymentError('Please enter a valid 10-digit US phone number for delivery.');
      } else {
        setPaymentError(err?.message || 'Failed to start checkout. Please try again.');
      }
    } finally {
      setCreatingOrder(false);
    }
  };

  // ── "Save this card" toggle ───────────────────────────────
  // Whether the card is kept lives on the PaymentIntent, which was already
  // created when the shopper reached this step. Re-calling the callable
  // re-applies the choice to that same intent and returns the same client
  // secret, so the mounted Elements are left untouched.
  const handleToggleSaveCard = async (next: boolean) => {
    if (!orderId) return;
    const previous = saveCard;
    setSaveCard(next);
    setSavePreferenceBusy(true);
    try {
      const createPI = httpsCallable(functions, 'createCheckoutPaymentIntent');
      await createPI({ orderId, savePaymentMethod: next });
    } catch (err: any) {
      setSaveCard(previous);
      setPaymentError(
        err?.message || 'Could not update your card-saving preference. Please try again.'
      );
    } finally {
      setSavePreferenceBusy(false);
    }
  };

  // ── Payment success handler ───────────────────────────────
  const handlePaymentSuccess = async () => {
    try {
      // Submit order to business for approval (PAYMENT_AUTHORIZED → PENDING_BUSINESS_APPROVAL)
      const submitOrder = httpsCallable(functions, 'submitOrderToBusiness');
      await submitOrder({ orderId });
    } catch (err: any) {
      console.error('Failed to submit to business:', err);
      // Don't block — order is paid, business will see it regardless
    }

    cart.clearCart();
    navigate(`/orders/${orderId}`);
  };

  // ── Empty Cart ────────────────────────────────────────────
  if (cart.items.length === 0 && checkoutStep === 'cart') {
    return (
      <div className="min-h-screen bg-white pb-20 md:pb-0">
        <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center">
              <BackButton fallback={SHOPPER_HOME} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" />
              <h1 className="ml-4 text-xl font-serif text-heading">
                Your Bag
              </h1>
            </div>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center h-[60vh] px-4 text-center">
          <div className="w-24 h-24 rounded-full bg-purple-50 flex items-center justify-center mb-6">
            <ShoppingBag className="h-12 w-12 text-primary" />
          </div>
          <h2 className="text-2xl font-serif text-heading mb-2">
            Your bag is empty
          </h2>
          <p className="text-body mb-8">
            Discover something beautiful to add
          </p>
          <Button onClick={() => navigate(SHOPPER_HOME)}>Continue Shopping</Button>
        </div>

        <ShopperBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => {
                  if (checkoutStep === 'payment') {
                    setCheckoutStep('cart');
                    setPaymentError(null);
                  } else {
                    goBack();
                  }
                }}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
                aria-label={checkoutStep === 'payment' ? 'Back to bag' : 'Go back'}
              >
                <ArrowLeft className="h-6 w-6 text-heading" />
              </button>
              <h1 className="ml-4 text-xl font-serif text-heading">
                {checkoutStep === 'payment' ? 'Secure Checkout' : 'Your Bag'}
              </h1>
            </div>
            {checkoutStep === 'cart' && (
              <span className="text-sm text-body">
                {cart.itemCount}{' '}
                {cart.itemCount === 1 ? 'item' : 'items'}
              </span>
            )}
            {checkoutStep === 'payment' && (
              <div className="flex items-center gap-1.5 text-sm text-green-600">
                <Lock className="h-3.5 w-3.5" />
                <span className="font-medium">Secure</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {checkoutStep === 'payment' && stripeInstance && orderId && clientSecret ? (
          /* ── Payment Step ───────────────────────────────────── */
          <div className="max-w-lg mx-auto space-y-6">
            {/* Order total recap — server-priced (the amount actually authorized) */}
            <div className="bg-white rounded-2xl p-4 shadow-soft">
              <div className="flex items-center justify-between">
                <span className="text-sm text-body">Order Total</span>
                <span className="text-lg font-semibold text-heading">
                  ${(serverTotal ?? total).toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-body mt-1">
                {cart.itemCount} {cart.itemCount === 1 ? 'item' : 'items'} from{' '}
                {cart.items[0]?.brand}
              </p>
            </div>

            {/* Payment error */}
            {paymentError && (
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Payment Error
                  </p>
                  <p className="text-xs text-red-600 mt-0.5">
                    {paymentError}
                  </p>
                </div>
              </div>
            )}

            {/* Stripe Elements wrapper */}
            {/* clientSecret in options is what lets PaymentElement render. */}
            <Elements
              stripe={stripeInstance}
              options={{ clientSecret, appearance: STRIPE_APPEARANCE }}
            >
              <CheckoutForm
                orderId={orderId}
                clientSecret={clientSecret}
                savedMethods={savedMethods}
                defaultPaymentMethodId={defaultPaymentMethodId}
                supportsSavedCards={supportsSavedCards}
                saveCard={saveCard}
                savePreferenceBusy={savePreferenceBusy}
                onToggleSaveCard={handleToggleSaveCard}
                onSuccess={handlePaymentSuccess}
                onError={setPaymentError}
              />
            </Elements>
          </div>
        ) : (
          /* ── Cart Step ──────────────────────────────────────── */
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4 mb-8 lg:mb-0">
              {cart.items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl p-4 shadow-soft flex space-x-4"
                >
                  <Link
                    to={`/product/${item.productId}`}
                    className="w-24 h-32 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0"
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </Link>

                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <p className="text-xs font-medium text-primary uppercase tracking-wider">
                        {item.brand}
                      </p>
                      <Link to={`/product/${item.productId}`}>
                        <h3 className="font-serif font-medium text-heading hover:text-primary transition-colors">
                          {item.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-body">Size: {item.size}</p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() =>
                            cart.updateQuantity(item.id, item.quantity - 1)
                          }
                          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="font-medium w-6 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            cart.updateQuantity(item.id, item.quantity + 1)
                          }
                          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="flex items-center space-x-4">
                        <p className="font-medium text-heading">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <button
                          onClick={() => cart.removeItem(item.id)}
                          className="p-2 text-gray-400 hover:text-error transition-colors"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Promo Code */}
              <div className="bg-white rounded-2xl p-4 shadow-soft">
                <div className="flex items-center space-x-3">
                  <Tag className="h-5 w-5 text-primary" />
                  <span className="font-medium text-heading">Promo Code</span>
                </div>
                {promoApplied ? (
                  <div className="flex items-center justify-between mt-3 p-3 rounded-xl bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800">{promoApplied.code}</p>
                        <p className="text-xs text-green-700">
                          {promoApplied.description} &mdash; you save ${displayDiscount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setPromoApplied(null);
                        setPromoError(null);
                      }}
                      className="text-xs text-green-700 font-medium hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex space-x-3 mt-3">
                      <Input
                        placeholder="Enter code"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          setPromoError(null);
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        onClick={handleApplyPromo}
                        disabled={!promoCode.trim() || promoLoading}
                      >
                        {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    {promoError && (
                      <p className="text-xs text-red-600 mt-2">{promoError}</p>
                    )}
                  </>
                )}
              </div>

              {/* Continue Shopping */}
              <div className="text-center pt-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate(SHOPPER_HOME)}
                  className="text-primary"
                >
                  &larr; Continue Shopping
                </Button>
              </div>
            </div>

            {/* Delivery Address + Order Summary */}
            <div className="space-y-6">
              {/* Delivery Address */}
              <div className="bg-white rounded-2xl p-4 shadow-soft space-y-3">
                <div className="flex items-center space-x-3 mb-1">
                  <MapPin className="h-5 w-5 text-primary" />
                  <span className="font-medium text-heading">
                    Delivery Address
                  </span>
                </div>

                {/* Saved addresses picker */}
                {savedAddresses.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {savedAddresses.map((addr) => (
                      <button
                        key={addr.id}
                        type="button"
                        onClick={() => applySavedAddress(addr)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selectedAddressId === addr.id
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        title={`${addr.street}, ${addr.city}`}
                      >
                        <MapPin className="h-3 w-3" />
                        {addr.label}
                        {addr.isDefault && <span className="opacity-60">&middot; default</span>}
                      </button>
                    ))}
                  </div>
                )}

                <AddressAutocomplete
                  value={address.street}
                  onChange={(val) => {
                    setSelectedAddressId(null);
                    // Manual edit invalidates any previously captured coordinates
                    setAddress((a) => ({ ...a, street: val, lat: undefined, lng: undefined }));
                  }}
                  onSelect={(parsed) => {
                    setSelectedAddressId(null);
                    setAddress({
                      street: parsed.street,
                      city: parsed.city,
                      state: parsed.state,
                      zipCode: parsed.zipCode,
                      lat: parsed.lat,
                      lng: parsed.lng,
                    });
                  }}
                  placeholder="Search for your address..."
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    placeholder="City"
                    value={address.city}
                    onChange={(e) =>
                      setAddress((a) => ({ ...a, city: e.target.value, lat: undefined, lng: undefined }))
                    }
                  />
                  <Input
                    placeholder="State"
                    value={address.state}
                    onChange={(e) =>
                      setAddress((a) => ({ ...a, state: e.target.value, lat: undefined, lng: undefined }))
                    }
                  />
                  <Input
                    placeholder="ZIP"
                    value={address.zipCode}
                    onChange={(e) =>
                      setAddress((a) => ({ ...a, zipCode: e.target.value, lat: undefined, lng: undefined }))
                    }
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <Input
                    type="tel"
                    placeholder="Phone number for delivery updates"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                  />
                </div>
                {customerPhone.trim() && !phoneValid && (
                  <p className="text-xs text-red-600 pl-6">
                    Enter a valid 10-digit US phone number.
                  </p>
                )}

                {/* Delivery Details */}
                <div className="border-t border-gray-100 pt-3 mt-1 space-y-3">
                  {/* Delivery Method Selector */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">How should we deliver?</p>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { id: 'DOOR' as const, label: 'Meet at door', icon: '🚪' },
                        { id: 'LEAVE_AT_DOOR' as const, label: 'Leave at door', icon: '📦' },
                        { id: 'CURBSIDE' as const, label: 'Meet outside', icon: '🚶' },
                      ]).map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setDropoffType(opt.id)}
                          className={`flex flex-col items-center p-2.5 rounded-xl border-2 transition-all text-center ${
                            dropoffType === opt.id
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <span className="text-lg mb-0.5">{opt.icon}</span>
                          <span className="text-[11px] font-medium text-gray-700 leading-tight">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Input
                    placeholder="Apt, Suite, or Unit (optional)"
                    value={apt}
                    onChange={(e) => setApt(e.target.value)}
                    className="text-sm"
                  />

                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Does your location require an access code?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setGateCodeEnabled((v) => !v);
                          if (gateCodeEnabled) setGateCode('');
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          gateCodeEnabled
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        <KeyRound className="h-3 w-3" />
                        Gate Code
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setBuildingCodeEnabled((v) => !v);
                          if (buildingCodeEnabled) setBuildingCode('');
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          buildingCodeEnabled
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        <DoorOpen className="h-3 w-3" />
                        Building Code
                      </button>
                    </div>
                    {gateCodeEnabled && (
                      <Input
                        placeholder="Enter gate code (e.g. #1234)"
                        value={gateCode}
                        onChange={(e) => setGateCode(e.target.value)}
                        className="mt-2 text-sm"
                        autoFocus
                      />
                    )}
                    {buildingCodeEnabled && (
                      <Input
                        placeholder="Enter building code (e.g. *5678)"
                        value={buildingCode}
                        onChange={(e) => setBuildingCode(e.target.value)}
                        className="mt-2 text-sm"
                        autoFocus
                      />
                    )}
                  </div>

                  <div>
                    <textarea
                      placeholder="Delivery instructions (optional) — e.g. &quot;Leave at front door&quot;, &quot;Ring doorbell&quot;, &quot;Call when arriving&quot;"
                      value={deliveryInstructions}
                      onChange={(e) =>
                        setDeliveryInstructions(e.target.value.slice(0, 280))
                      }
                      rows={2}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                    <p className="text-right text-[10px] text-gray-400 mt-0.5">
                      {deliveryInstructions.length}/280
                    </p>
                  </div>

                  {/* Driver Tip */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Add a tip for your driver (optional)</p>
                    <div className="flex gap-2">
                      {[0, 2, 4, 6].map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => { setDriverTip(amount); setCustomTipInput(''); }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            driverTip === amount && !customTipInput
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          {amount === 0 ? 'No tip' : `$${amount}`}
                        </button>
                      ))}
                      <input
                        type="number"
                        placeholder="Custom"
                        value={customTipInput}
                        onChange={(e) => {
                          setCustomTipInput(e.target.value);
                          setDriverTip(e.target.value ? Number(e.target.value) : 0);
                        }}
                        className="w-20 px-2 py-1.5 rounded-full text-xs border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        min={0}
                        max={50}
                      />
                    </div>
                  </div>
                </div>

                {!addressComplete && (
                  <p className="text-xs text-body">
                    Enter your address for delivery fee estimate
                  </p>
                )}
                {estimateLoading && (
                  <div className="flex items-center space-x-2 text-sm text-primary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Getting delivery estimate...</span>
                  </div>
                )}
                {deliveryError && !estimateLoading && (
                  <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2.5">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{deliveryError}</span>
                  </div>
                )}
              </div>

              <CheckoutSummaryCard
                subtotal={cart.subtotal}
                deliveryFee={deliveryFee === null ? null : displayFee}
                tip={driverTip || 0}
                discount={displayDiscount}
                total={total}
                estimateLoading={estimateLoading}
                deliveryEstimate={deliveryEstimateLabel}
                paymentMethod={
                  summaryCard
                    ? { brand: formatCardBrand(summaryCard.brand), last4: summaryCard.last4 }
                    : undefined
                }
                onPaymentMethodClick={() => navigate('/settings/payment')}
              />

              {/* Terms Checkbox */}
              <div className="bg-white rounded-2xl p-4 shadow-soft">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-body">
                    I agree to the{' '}
                    <Link
                      to="/faq"
                      target="_blank"
                      className="text-primary font-medium hover:underline"
                    >
                      Delivery &amp; Refund Policy
                    </Link>
                  </span>
                </label>
              </div>

              {/* Payment error */}
              {paymentError && (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{paymentError}</p>
                </div>
              )}

              {/* Proceed to Payment Button */}
              <Button
                size="lg"
                fullWidth
                onClick={handleProceedToPayment}
                disabled={
                  !agreedToTerms || !addressComplete || !phoneValid || creatingOrder
                }
                className="shadow-lg"
              >
                {creatingOrder ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Proceed to Payment &bull; ${total.toFixed(2)}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-body">
                Funds held temporarily. Charged only after courier pickup.
              </p>
            </div>
          </div>
        )}
      </main>

      {checkoutStep === 'cart' && <ShopperBottomNav />}
    </div>
  );
}
